import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import "./assets/less/main.less";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { KeycloakProvider } from "./providers/keycloak-provider";
import { PortalContext, PortalProvider } from "./providers/portal-provider";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { theme } from "./util/theme";
import { pruneDisallowedBrowserCookies } from "./util/cookie-allowlist";
import * as Encryptoo from "encryptoo";

type CryptoApi = {
  encrypt: (plain: string) => string;
  decrypt: (cipher: string) => string;
  getSecret: (isServer?: boolean) => string;
  init: () => string;
};

// Antes do hydrate: apaga no navegador cookies que o SSR não vê (não-HttpOnly)
// e que outros sistemas do mesmo domínio foram acumulando.
pruneDisallowedBrowserCookies();

let clientPublicKey: string | null = null;

function ensureCryptoInitialized(): string {
  if (!clientPublicKey) {
    clientPublicKey = Encryptoo.init();
  }

  return clientPublicKey;
}

function getServerPublicKey(): string {
  const boot = window.__XVIA_CRYPTO_BOOT__;

  if (typeof boot === "string") {
    return boot;
  }

  if (boot?.serverPublicKey) {
    return boot.serverPublicKey;
  }

  throw new Error("crypto_boot_not_initialized");
}

const api: CryptoApi = Object.freeze({
  init() {
    return ensureCryptoInitialized();
  },
  encrypt(plain: string) {
    ensureCryptoInitialized();
    const serverKey = getServerPublicKey();
    return Encryptoo.encrypt(plain, serverKey);
  },
  decrypt(cipher: string) {
    ensureCryptoInitialized();
    const serverKey = getServerPublicKey();
    return Encryptoo.decrypt(cipher, serverKey);
  },
  getSecret(isServer = false) {
    ensureCryptoInitialized();
    const serverKey = getServerPublicKey();
    return Encryptoo.getSecret(serverKey, isServer);
  },
});

let context: Partial<PortalContext> = {};
if (process.env.NODE_ENV !== "production") {
  context = {
    env: {
      BACKEND_ENDPOINT: BACKEND_ENDPOINT,
      SMARTPASS_LOGIN_URL: SMARTPASS_LOGIN_URL,
      SMARTPASS_LOGIN_REALM: SMARTPASS_LOGIN_REALM,
      SMARTPASS_LOGIN_CLIENT_ID: SMARTPASS_LOGIN_CLIENT_ID,
    },
  };
  window.__XVIA_PORTAL_LOADED__ = true;
}

window.__XVIA_CRYPTO_API__ = api;
window.dispatchEvent(new Event("__XVIA_CRYPTO_READY__"));

ReactDOM.hydrate(
  <React.StrictMode>
    <PortalProvider context={context as PortalContext}>
      <KeycloakProvider>
        <ThemeProvider theme={theme}>
          <BrowserRouter basename={"/"}>
            <App />
          </BrowserRouter>
        </ThemeProvider>
      </KeycloakProvider>
    </PortalProvider>
  </React.StrictMode>,
  document.getElementById("xvia-main")
);

// If you want to start measuring performance in your app, pass a function
// to log results here (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
